import { ComponentType } from '@angular/cdk/portal';
import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogParams } from 'src/models/global-interfaces';
import { DashboardOpenTask, DashboardOpenTaskSet } from './dashboard.service';
import { DialogComponent } from 'src/components/dialog/dialog.component';
import {
  DeleteTaskDialogComponent,
  DeleteTaskDialogPayload,
} from 'src/dialogs/delete-task-dialog/delete-task-dialog.component';
import {
  GenericDialogComponent,
  GenericDialogPayload,
} from 'src/dialogs/generic-dialog/generic-dialog.component';

export interface DialogPayload<T> {
  payload: T;
}

export interface DialogRefResult {
  openTasksOption?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface EmptyPayload {}

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private readonly dialog = inject(MatDialog);
  private defaultWidth = '500px';
  private defaultMaxHeight = '94vh';
  private defaultDisableClose = true;

  genericOpen<T, P>(
    params: DialogParams<GenericDialogComponent<T, P>, GenericDialogPayload<T, P>>,
  ) {
    return this.open<GenericDialogComponent<T, P>, GenericDialogPayload<T, P>>(params);
  }

  open<T = void, P = void>(params: DialogParams<NoInfer<T>, NoInfer<P>>) {
    const payload = params.payload;
    const component: ComponentType<T | DialogComponent> = params.component || DialogComponent;
    const dialogRef: MatDialogRef<T | DialogComponent, DialogRefResult> = this.dialog.open(
      component,
      {
        width: params.width ?? this.defaultWidth,
        maxHeight: params.maxHeight ?? this.defaultMaxHeight,
        height: params.height,
        minHeight: params.minHeight ?? 'auto',
        disableClose: params.disableClose ?? this.defaultDisableClose,
        hasBackdrop: params.hasBackdrop ?? true,
        panelClass: params.customStyleClass ?? '',
        data: {
          title: params.title,
          content: params.content,
          okLabel: params.okLabel ?? 'Ok',
          cancelLabel: params.cancelLabel ?? 'Cancel',
          onConfirm: params.onConfirm,
          onCancel: params.onCancel,
          payload: payload,
          filterType: params.filterType,
          secondary: params.secondary,
          getItems: params.getItems,
          body: params.body,
          actions: params.actions,
          checkBox: params.checkBox,
          buttons: params.buttons,
          radioOptions: params.radioOptions,
          setDefaultRadioOption: params.setDefaultRadioOption,
          textInput: params.textInput,
          textInputPlaceholder: params.textInputPlaceholder,
          onXClick: params.onXClick,
        },
      },
    );

    dialogRef.afterClosed().subscribe(result => {
      if (result?.openTasksOption === true) {
        params.onConfirm(true);
      } else if (result?.openTasksOption === false) {
        params.onConfirm(false);
      } else {
        if (result) {
          if (params.onConfirm) {
            params.onConfirm(result);
          }
          if (params.closeDialog) {
            this.dialog.closeAll();
          }
        } else {
          if (params.onCancel) {
            params.onCancel();
          }
        }
      }
    });
    return dialogRef;
  }

  openChild<T, P>(dialog: MatDialog, params: DialogParams<T | DialogComponent, P>) {
    const component: ComponentType<T | DialogComponent> = params.component || DialogComponent;
    const dialogRef: MatDialogRef<T | DialogComponent, DialogRefResult> = dialog.open(component, {
      width: params.width ?? this.defaultWidth,
      maxHeight: params.maxHeight ?? this.defaultMaxHeight,
      minHeight: params.minHeight ?? 'auto',
      disableClose: params.disableClose ?? this.defaultDisableClose,
      hasBackdrop: params.hasBackdrop ?? true,
      data: {
        title: params.title,
        content: params.content,
        okLabel: params.okLabel ?? 'Ok',
        cancelLabel: params.cancelLabel ?? 'Cancel',
        onConfirm: params.onConfirm,
        onCancel: params.onCancel,
        payload: params.payload,
        secondary: params.secondary,
        body: params.body,
        actions: params.actions,
        buttons: params.buttons,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (params.onConfirm) {
          params.onConfirm(true);
        }
      } else {
        if (params.onCancel) {
          params.onCancel();
        }
      }
    });
    return dialogRef;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  confirmChanges(currentDialog: MatDialogRef<any>) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dialogRef = this.open<DialogComponent, any>({
      payload: {},
      title: `Unsaved Changes`,
      content: [
        {
          scope: 'danger',
          title: 'Warning',
          message: 'If you continue your unsaved changes will be lost.',
        },
        {
          message: 'You have unsaved changes, are you sure you want to continue?',
        },
      ],
      closeDialog: false,
      buttons: [
        {
          text: 'Cancel',
          theme: 'secondary',
          action: () => true,
        },
        {
          text: 'Discard Changes',
          theme: 'primary',
          action: () => {
            currentDialog.close();
            return true;
          },
        },
      ],
    });
    return dialogRef;
  }

  openDeleteTaskDialog = (data: DashboardOpenTask, cb: () => void) => {
    this.open<DeleteTaskDialogComponent, DeleteTaskDialogPayload>({
      component: DeleteTaskDialogComponent,
      payload: { postDeleteCallback: cb, id: data.id },
      title: `Delete Task: ${data.name}`,
      content: [
        {
          scope: 'danger',
          title: 'Warning',
          message: `You are about to delete <strong>${data.name}</strong>. Deleting this task will not remove other instances of it.`,
        },
      ],
      closeDialog: true,
    });
  };

  openDeleteTaskSetDialog = (data: DashboardOpenTaskSet, cb: () => void) => {
    this.open<DeleteTaskDialogComponent, DeleteTaskDialogPayload>({
      component: DeleteTaskDialogComponent,
      title: `Delete Task Set: ${data.task_set_name}`,
      content: [
        {
          scope: 'danger',
          title: 'Warning',
          message: `You are about to delete <strong>${data.task_set_name}</strong>. Deleting this task set will not remove other instances of it.`,
        },
      ],
      closeDialog: true,
      payload: {
        postDeleteCallback: cb,
        id: data.id,
        isTaskSet: true,
      },
    });
  };
}
